import React, { useRef, useState, useEffect } from "react";
import "./MainSection.css";

// 🔹 Import your local videos from src/placeholders/videos
import audioMainPlayer1 from "../../../placeholders/videos/audio_main_player1.mp4";
import audioMainPlayer2 from "../../../placeholders/videos/audio_main_player2.mp4";
import audioMainPlayer3 from "../../../placeholders/videos/audio_main_player3.mp4";

const videoSources = [
  { id: 1, src: audioMainPlayer1 },
  { id: 2, src: audioMainPlayer2 },
  { id: 3, src: audioMainPlayer3 },
];

const MainSection = () => {
  const videoRef = useRef(null);

  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showBeta, setShowBeta] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const testFlightLink = "https://testflight.apple.com/join/KVhJ4483";

  const currentVideo = videoSources[currentVideoIndex];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(testFlightLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleVideoError = () => {
    setVideoError("Unable to load video");
    setIsPlaying(false);
  };

  const tryPlayVideo = () => {
    const el = videoRef.current;
    if (!el) return;

    setVideoError(null);

    el.play()
      .then(() => setIsPlaying(true))
      .catch((err) => {
        console.warn("Video play blocked or failed:", err);
        setIsPlaying(false);
      });
  };

  const handlePlayPause = () => {
    const el = videoRef.current;
    if (!el) return;

    setHasInteracted(true);

    if (isPlaying) {
      el.pause();
      setIsPlaying(false);
    } else {
      tryPlayVideo();
    }
  };

  const handleNextVideo = () => {
    setHasInteracted(true);
    setVideoError(null);
    setIsPlaying(false);
    setCurrentVideoIndex((prev) => (prev + 1) % videoSources.length);
  };

  const handlePrevVideo = () => {
    setHasInteracted(true);
    setVideoError(null);
    setIsPlaying(false);
    setCurrentVideoIndex((prev) =>
      (prev - 1 + videoSources.length) % videoSources.length
    );
  };

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    el.load();

    if (hasInteracted) {
      tryPlayVideo();
    } else {
      el.pause();
      setIsPlaying(false);
    }
  }, [currentVideoIndex, hasInteracted]);

  return (
    <section id="wrappr-main-section" className="wrappr-main-section">
      <div className="wrappr-container">
        {/* Left column: arrows + iPhone */}
        <div className="wrappr-video-wrapper">
          {/* LEFT arrow */}
          <button
            className="video-nav-button video-nav-prev"
            onClick={handlePrevVideo}
            aria-label="Previous video"
          >
            ‹
          </button>

          {/* iPhone mockup block */}
          <div className="iphone-mockup">
            <div className="iphone-frame">
              <div className="iphone-screen">
                {videoError ? (
                  <div className="video-error">
                    <span>📱</span>
                    <p>{videoError}</p>
                  </div>
                ) : (
                  <>
                    <video
                      key={currentVideo.id}
                      ref={videoRef}
                      className="iphone-video"
                      loop
                      muted
                      playsInline
                      preload="auto"
                      onError={handleVideoError}
                      onEnded={() => setIsPlaying(false)}
                    >
                      <source src={currentVideo.src} type="video/mp4" />
                    </video>

                    {!isPlaying && !videoError && (
                      <button
                        className="video-play-button"
                        onClick={handlePlayPause}
                        aria-label="Play video"
                      >
                        <svg
                          width="32"
                          height="32"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </button>
                    )}

                    {isPlaying && !videoError && (
                      <div
                        className="video-tap-overlay"
                        onClick={handlePlayPause}
                      />
                    )}
                  </>
                )}
              </div>

              <div className="iphone-home-indicator" />
            </div>

            <div className="iphone-shadow" />
          </div>

          {/* RIGHT arrow */}
          <button
            className="video-nav-button video-nav-next"
            onClick={handleNextVideo}
            aria-label="Next video"
          >
            ›
          </button>
        </div>

        {/* Right column: text content */}
        <div className="wrappr-content">
          <p className="wrappr-eyebrow">CREATE. LISTEN. SHARE.</p>
          <h2 className="wrappr-title">
            MAKE AUDIO PEOPLE{" "}
            <span className="wrappr-title-accent">WANT TO LISTEN TO</span>
          </h2>
          <p className="wrappr-description">
            Turn your curiosity into insightful audio with AI voices that sound
            real
          </p>

          {!showBeta ? (
            <button className="wrappr-cta" onClick={() => setShowBeta(true)}>
              <span className="cta-text">TRY NOW</span>
            </button>
          ) : (
            <div className="beta-reveal">
              <h3 className="beta-title">Join the Beta</h3>
              
              <div className="beta-actions">
                <a
                  href={testFlightLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="beta-btn primary"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  Open TestFlight
                </a>

                <button 
                  className={`beta-btn secondary ${copied ? 'copied' : ''}`}
                  onClick={handleCopy}
                >
                  {copied ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      Copied
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2"/>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                      </svg>
                      Copy Link
                    </>
                  )}
                </button>
              </div>

              <button className="beta-info" onClick={() => setShowModal(true)}>
                What's TestFlight?
              </button>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">TestFlight Beta</h3>
            <p className="modal-text">
              Get early access and help shape our app. By joining, you agree to our Terms of Service and Privacy Policy.
            </p>
            <button className="modal-btn" onClick={() => setShowModal(false)}>
              Got it
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default MainSection;